import { useMemo } from 'react';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import {
  getConversationsQueryKey,
  getConversationQueryKey,
  getConversationMessagesQueryKey,
} from '@web/conversations/utils/getQueryKey';
import {
  Conversation,
  ConversationBasic,
  ConversationFlagKeysEnum,
} from '@web/conversations/types/Conversation';
import { Message } from '@web/conversations/types/Message';
import { ReactionsMap } from '@web/conversations/types/Reactions';
import { isMessagePost } from '@web/conversations/utils/guards';

export const useConversationQueryCache = () => {
  const queryClient = useQueryClient();

  return useMemo(() => {
    const refreshConversations = () => {
      const key = getConversationsQueryKey();
      void queryClient.refetchQueries(key, { exact: true });
    };

    const updateFlagProp = (
      conversationId: ID,
      flagName: ConversationFlagKeysEnum,
      value: boolean
    ) => {
      const conversationQueryKey = getConversationQueryKey(conversationId);

      queryClient.setQueryData<Conversation>(conversationQueryKey, (cachedConversation) =>
        cachedConversation
          ? {
              ...cachedConversation,
              [flagName]: value,
            }
          : undefined
      );

      const conversationsQueryKey = getConversationsQueryKey();

      queryClient.setQueryData<ConversationBasic[]>(conversationsQueryKey, (cachedConversations) =>
        cachedConversations?.map((conversation) =>
          conversation.id === conversationId ? { ...conversation, [flagName]: value } : conversation
        )
      );
    };

    const pinConversation = (conversationId: ID) =>
      updateFlagProp(conversationId, ConversationFlagKeysEnum.IS_PINNED, true);
    const unpinConversation = (conversationId: ID) =>
      updateFlagProp(conversationId, ConversationFlagKeysEnum.IS_PINNED, false);
    const archiveConversation = (conversationId: ID) =>
      updateFlagProp(conversationId, ConversationFlagKeysEnum.IS_ARCHIVED, true);
    const muteConversation = (conversationId: ID) =>
      updateFlagProp(conversationId, ConversationFlagKeysEnum.IS_SILENCED, true);
    const unarchiveConversation = (conversationId: ID) =>
      updateFlagProp(conversationId, ConversationFlagKeysEnum.IS_ARCHIVED, false);

    const unmuteConversation = (conversationId: ID) => {
      const conversationQueryKey = getConversationQueryKey(conversationId);

      queryClient.setQueryData<Conversation>(conversationQueryKey, (cachedConversation) =>
        cachedConversation
          ? {
              ...cachedConversation,
              isSilenced: false,
              newestUnsilencedMessagePostedAt: cachedConversation.newestMessagePostedAt,
            }
          : undefined
      );

      const conversationsQueryKey = getConversationsQueryKey();

      queryClient.setQueryData<ConversationBasic[]>(conversationsQueryKey, (cachedConversations) =>
        cachedConversations?.map((conversation) =>
          conversation.id === conversationId
            ? {
                ...conversation,
                isSilenced: false,
                newestUnsilencedMessagePostedAt: conversation.newestMessagePostedAt,
              }
            : conversation
        )
      );
    };

    const removeConversation = (conversationId: ID) => {
      const conversationQueryKey = getConversationQueryKey(conversationId);

      queryClient.removeQueries(conversationQueryKey);

      const conversationsQueryKey = getConversationsQueryKey();

      queryClient.setQueryData<ConversationBasic[]>(conversationsQueryKey, (cachedConversations) =>
        cachedConversations?.filter((conversation) => conversation.id !== conversationId)
      );
    };

    const addConversations = (conversations: ConversationBasic | ConversationBasic[]) => {
      const conversationsQueryKey = getConversationsQueryKey();

      const conversationsToRefetch = [];

      queryClient.setQueryData<ConversationBasic[]>(
        conversationsQueryKey,
        (cachedConversations) => {
          if (!cachedConversations) return;

          const newConversations = Array.isArray(conversations)
            ? [...conversations]
            : [conversations];

          const updatedConversations = [...cachedConversations];
          newConversations.forEach((conversation) => {
            // @INFO when bulk messaging, existing conversations will be returned as well
            // we've got to put to cache only the new ones, and refetch messages of the existing ones
            const cachedConversationIndex = updatedConversations.findIndex(
              (c) => c.id === conversation.id
            );
            if (cachedConversationIndex < 0) {
              updatedConversations.push(conversation);
            } else {
              updatedConversations[cachedConversationIndex] = {
                ...updatedConversations[cachedConversationIndex],
                ...conversation,
              };
              conversationsToRefetch.push(conversation);
            }
          });

          return updatedConversations;
        }
      );

      return Promise.all(
        conversationsToRefetch.map((conversation) => {
          const messagesQueryKey = getConversationMessagesQueryKey(conversation.id);
          return queryClient.refetchQueries<Message[]>(messagesQueryKey, {
            exact: true,
          });
        })
      );
    };

    const updateConversation = (conversationId: ID, updatedConversation: Partial<Conversation>) => {
      const conversationQueryKey = getConversationQueryKey(conversationId);

      queryClient.setQueryData<Conversation>(conversationQueryKey, (cachedData) =>
        cachedData
          ? {
              ...cachedData,
              ...updatedConversation,
            }
          : undefined
      );

      const conversationsQueryKey = getConversationsQueryKey();
      queryClient.setQueryData<ConversationBasic[]>(conversationsQueryKey, (cachedConversations) =>
        cachedConversations?.map((conversation) =>
          conversation.id === conversationId
            ? { ...conversation, ...updatedConversation }
            : conversation
        )
      );
    };

    const addMessage = (message: Message) => {
      const queryKey = getConversationMessagesQueryKey(message.conversationId);

      queryClient.setQueryData<InfiniteData<Message[]>>(queryKey, (cachedData) => {
        if (!cachedData) {
          return;
        }

        const { pages } = cachedData;
        const [newestPage, ...restPages] = pages;
        const updatedPage = [message, ...newestPage];
        const updatedPages = [updatedPage, ...restPages];

        return {
          ...cachedData,
          pages: updatedPages,
        };
      });

      if (!isMessagePost(message)) return;

      const conversationQueryKey = getConversationQueryKey(message.conversationId);

      queryClient.setQueryData<Conversation>(conversationQueryKey, (cachedConversation) => {
        if (!cachedConversation) {
          return undefined;
        }
        const newestMessagePostedAt = new Date(message.postedAt);
        const newestUnsilencedMessagePostedAt = cachedConversation.isSilenced
          ? cachedConversation.newestUnsilencedMessagePostedAt
          : newestMessagePostedAt;

        return {
          ...cachedConversation,
          isArchived: false,
          newestMessageId: message.id,
          newestMessagePostedAt,
          newestUnsilencedMessagePostedAt,
        };
      });

      const conversationsQueryKey = getConversationsQueryKey();

      queryClient.setQueryData<ConversationBasic[]>(conversationsQueryKey, (cachedConversations) =>
        cachedConversations?.map((conversation) => {
          if (conversation.id !== message.conversationId) {
            return conversation;
          }

          const newestMessagePostedAt = new Date(message.postedAt);
          const newestUnsilencedMessagePostedAt = conversation.isSilenced
            ? conversation.newestUnsilencedMessagePostedAt
            : newestMessagePostedAt;

          return {
            ...conversation,
            isArchived: false,
            newestMessageId: message.id,
            newestMessagePostedAt,
            newestUnsilencedMessagePostedAt,
          };
        })
      );
    };

    const removeMessage = (conversationId: ID, messageId: ID) => {
      const queryKey = getConversationMessagesQueryKey(conversationId);

      queryClient.setQueryData<InfiniteData<Message[]>>(queryKey, (cachedData) =>
        cachedData
          ? {
              ...cachedData,
              pages: cachedData.pages.map((messages) =>
                messages.map((message) =>
                  message.id === messageId && isMessagePost(message)
                    ? { ...message, isDeleted: true }
                    : message
                )
              ),
            }
          : undefined
      );
    };

    const updateMessage = (conversationId: ID, updatedMessage: Message) => {
      const queryKey = getConversationMessagesQueryKey(conversationId);

      queryClient.setQueryData<InfiniteData<Message[]>>(queryKey, (cachedData) =>
        cachedData
          ? {
              ...cachedData,
              pages: cachedData.pages.map((messages) =>
                messages.map((message) =>
                  message.id === updatedMessage.id && isMessagePost(message)
                    ? { ...message, ...updatedMessage }
                    : message
                )
              ),
            }
          : undefined
      );
    };

    const updateReactions = (conversationId: ID, messageId: ID, reactions: ReactionsMap) => {
      const queryKey = getConversationMessagesQueryKey(conversationId);

      queryClient.setQueryData<InfiniteData<Message[]>>(queryKey, (cachedData) =>
        cachedData
          ? {
              ...cachedData,
              pages: cachedData.pages.map((messages) =>
                messages.map((message) => {
                  if (message.id !== messageId) return message;

                  return {
                    ...message,
                    reactions,
                  };
                })
              ),
            }
          : undefined
      );
    };

    const updateUnreadStateByCounterOverride = (
      conversationId: ID,
      firstUnreadMessageId: ID | null,
      unreadMessagesCount: number
    ) => {
      const conversationQueryKey = getConversationQueryKey(conversationId);

      queryClient.setQueryData<Conversation>(conversationQueryKey, (cachedConversation) =>
        cachedConversation
          ? {
              ...cachedConversation,
              firstUnreadMessageId,
              unreadMessagesCount,
            }
          : undefined
      );

      const conversationsQueryKey = getConversationsQueryKey();

      queryClient.setQueryData<ConversationBasic[]>(conversationsQueryKey, (cachedConversations) =>
        cachedConversations?.map((conversation) =>
          conversation.id === conversationId
            ? { ...conversation, unreadMessagesCount }
            : conversation
        )
      );
    };

    const updateUnreadStateByNewMessage = (message: Message) => {
      if (!isMessagePost(message)) return;

      const conversationQueryKey = getConversationQueryKey(message.conversationId);

      queryClient.setQueryData<Conversation>(conversationQueryKey, (cachedConversation) => {
        if (!cachedConversation) {
          return;
        }

        const { firstUnreadMessageId, unreadMessagesCount } = cachedConversation;

        return {
          ...cachedConversation,
          firstUnreadMessageId: firstUnreadMessageId || message.id,
          unreadMessagesCount: unreadMessagesCount + 1,
        };
      });

      const conversationsQueryKey = getConversationsQueryKey();

      queryClient.setQueryData<ConversationBasic[]>(conversationsQueryKey, (cachedConversations) =>
        cachedConversations?.map((conversation) =>
          conversation.id === message.conversationId
            ? {
                ...conversation,
                unreadMessagesCount: conversation.unreadMessagesCount + 1,
              }
            : conversation
        )
      );
    };

    return {
      addConversations,
      addMessage,
      archiveConversation,
      muteConversation,
      pinConversation,
      refreshConversations,
      removeConversation,
      removeMessage,
      unarchiveConversation,
      unmuteConversation,
      unpinConversation,
      updateConversation,
      updateMessage,
      updateReactions,
      updateUnreadStateByCounterOverride,
      updateUnreadStateByNewMessage,
    };
  }, [queryClient]);
};
